// ─────────────────────────────────────────────────────────────────────────────
//  RowExpansionController
//
//  Encapsulates all row-expansion state and logic for uwc-datatable.
//
//  Responsibilities:
//    • Track which rows are expanded (_expandedKeys) and which are mid-close
//      animation (_collapsingKeys).
//    • Toggle expand/collapse with CSS-animation-aware cleanup via onAnimationEnd.
//    • Fire host-level 'uwc-row-expand' / 'uwc-row-collapse' events.
//    • Sync state from the host's `expandedRows` property.
//
//  The host owns all markup; the controller owns only state + events.
//
//  Usage inside UwcDatatable:
//
//    private _expansion = new RowExpansionController(
//      this,
//      (row) => this._getRowKey(row),   // closure — reads dataKey at call time
//    );
//
//    // In willUpdate:
//    if (changed.has('expandedRows')) this._expansion.syncFromProp(this.expandedRows);
//
//    // In render:
//    const expanded        = this._expansion.isExpanded(row);
//    const shouldRender    = this._expansion.shouldRender(row);
//    const isCollapsing    = this._expansion.isCollapsing(row);
//
//    // On toggle button click:
//    this._expansion.toggle(row, rowIndex);
//
//    // On expansion row animationend:
//    @animationend=${() => this._expansion.onAnimationEnd(row)}
//
// ─────────────────────────────────────────────────────────────────────────────
import type { ReactiveController, ReactiveControllerHost } from 'lit';

export class RowExpansionController implements ReactiveController {
  private readonly _host: ReactiveControllerHost & EventTarget;
  private _getKey: (row: Record<string, unknown>) => unknown;

  private _expandedKeys   = new Set<unknown>();
  private _collapsingKeys = new Set<unknown>();

  constructor(
    host: ReactiveControllerHost & EventTarget,
    getKey: (row: Record<string, unknown>) => unknown,
  ) {
    this._host   = host;
    this._getKey = getKey;
    host.addController(this);
  }

  hostConnected()    {}
  hostDisconnected() {}

  // ── Public API ─────────────────────────────────────────────────────────────

  /** True when the row is fully expanded (not collapsing). */
  isExpanded(row: Record<string, unknown>): boolean {
    return this._expandedKeys.has(this._getKey(row));
  }

  /** True when the row is playing its close animation. */
  isCollapsing(row: Record<string, unknown>): boolean {
    return this._collapsingKeys.has(this._getKey(row));
  }

  /**
   * True when the expansion <tr> should be in the DOM —
   * either fully open or mid-close animation.
   */
  shouldRender(row: Record<string, unknown>): boolean {
    const key = this._getKey(row);
    return this._expandedKeys.has(key) || this._collapsingKeys.has(key);
  }

  /** Toggle expand/collapse for a row. Fires uwc-row-expand or uwc-row-collapse. */
  toggle(row: Record<string, unknown>, index: number): void {
    const key = this._getKey(row);

    if (this._expandedKeys.has(key)) {
      // Collapse: move to collapsing so the close animation plays before removal
      const nextExpanded   = new Set(this._expandedKeys);
      const nextCollapsing = new Set(this._collapsingKeys);
      nextExpanded.delete(key);
      nextCollapsing.add(key);
      this._expandedKeys   = nextExpanded;
      this._collapsingKeys = nextCollapsing;
      this._host.dispatchEvent(new CustomEvent('uwc-row-collapse', {
        bubbles: true, composed: true,
        detail: { data: row, index },
      }));
    } else {
      // Expand — also cancel any in-progress collapse for this key
      const nextExpanded   = new Set(this._expandedKeys);
      const nextCollapsing = new Set(this._collapsingKeys);
      nextExpanded.add(key);
      nextCollapsing.delete(key);
      this._expandedKeys   = nextExpanded;
      this._collapsingKeys = nextCollapsing;
      this._host.dispatchEvent(new CustomEvent('uwc-row-expand', {
        bubbles: true, composed: true,
        detail: { data: row, index },
      }));
    }
    this._host.requestUpdate();
  }

  /**
   * Called when the expansion row's CSS animation ends.
   * Removes the row from the collapsing set so it is removed from the DOM.
   */
  onAnimationEnd(row: Record<string, unknown>): void {
    const key = this._getKey(row);
    if (!this._collapsingKeys.has(key)) return;
    const next = new Set(this._collapsingKeys);
    next.delete(key);
    this._collapsingKeys = next;
    this._host.requestUpdate();
  }

  /** Sync internal expanded-keys state from the host's `expandedRows` property. */
  syncFromProp(expandedRows?: Set<unknown>): void {
    this._expandedKeys = expandedRows ? new Set(expandedRows) : new Set();
  }
}
